/* eslint-disable no-unsafe-optional-chaining */
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { AGENTROLE, MANAGERROLE, SUPERVISORROLE } from '../../constants';
import { selectCurrentRole } from '../../pages/login/authSlice';
import './AttainmentChart.css';
import { MetricTargetsResult } from './types';
import { ConstScorecardRole } from '../../utils/typeConstants';

interface StackedRadialChartProps {
    data: MetricTargetsResult;
    selectedUserInGrid?: any;
    empNum?: number;
    metricType?: string;
    scorecardRoleId?: number;
}

const AttainmentChart: React.FC<StackedRadialChartProps> = ({
    data,
    selectedUserInGrid,
    empNum,
    metricType,
    scorecardRoleId,
}) => {
    // Extract and parse data with different target values
    const {
        agentMetricValue = 0,
        agentTarget = 0,
        supervisorMetricValue = 0,
        supervisorTarget = 0,
        scorecardMetricValue = 0,
        scorecardTarget = 0,
        scorecardTopPercentileMetricValue = 0,
        minValue = 0,
        maxValue = 0,
    } = data;

    // Format values based on metricType
    const formatValue = (value: number): string => {
        // Add % symbol if metricType is 'percentage'
        if (metricType?.toLowerCase() === 'percentage') {
            const val = value * 100;
            return `${val.toFixed(2)}%`;
        }

        // Otherwise return just the formatted number
        return value.toString();
    };

    // Reference and state for chart container and hover positioning
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const [hoveredMetric, setHoveredMetric] = useState<any>(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    const role = useSelector(selectCurrentRole);    

    // Metrics data with different target values
    let agentName = 'Me';
    let teamName = 'My Team';
    let scorecardName = 'My Scorecard';

    if (!selectedUserInGrid || Object.keys(selectedUserInGrid).length === 0) {
        agentName = 'Me';
        teamName = 'Team';
        scorecardName = 'Scorecard';
    } else if (selectedUserInGrid?.employee_number === empNum) {
        agentName = 'Me';
        teamName = 'My Team';
        scorecardName = 'My Scorecard';
    } else {
        const employeeName = selectedUserInGrid?.employee_name || '';
        if (employeeName.startsWith('Anonymous')) {
            agentName = 'Anonymous';
            teamName = 'Anonymous - Team';
            scorecardName = 'Anonymous - Scorecard';
        } else {
            const firstName = selectedUserInGrid?.first_name || '';
            agentName = firstName;
            teamName = `${firstName} - Team`;
            scorecardName = `${firstName} - Scorecard`;
        }
    }

    const metrics = [
        ...((scorecardRoleId === ConstScorecardRole.ASSOCIATE &&
            selectedUserInGrid &&
            Object.keys(selectedUserInGrid).length > 0 &&
            (role === SUPERVISORROLE || role === MANAGERROLE)) ||
            role === AGENTROLE
            ? [
                {
                    name: agentName,
                    value: agentMetricValue,
                    target: agentTarget,
                    color: 'rgb(147, 112, 219)',
                    radius: 140,
                    strokeWidth: 12,
                },
            ]
            : []),
        ...(((selectedUserInGrid &&
            Object.keys(selectedUserInGrid).length > 0 &&
            role === MANAGERROLE) ||
            role === SUPERVISORROLE ||
            role === AGENTROLE)
            ? [
                {
                    name: teamName,
                    value: supervisorMetricValue,
                    target: supervisorTarget,
                    color: 'rgb(30, 144, 255)',
                    radius: 120,
                    strokeWidth: 12,
                },
            ]
            : []),
        {
            name: scorecardName,
            value: scorecardMetricValue,
            target: scorecardTarget,
            color: 'rgb(32, 178, 170)',
            radius: 100,
            strokeWidth: 12,
        },
        {
            name: 'Top Performers',
            value: scorecardTopPercentileMetricValue,
            target: scorecardTarget,
            color: 'rgb(60, 179, 113)',
            radius: 80,
            strokeWidth: 12,
        },
    ];

    // Normalize a value between 0-1 for display based on min and max values
    const normalizeValue = (value: number): number => {
        return value === 0 ? 0 : Math.min(Math.max((value - minValue) / (maxValue - minValue), 0), 1);
    };

    // Calculate stroke dash array for semi-circular progress with target achievement
    const calculateStrokeDashArray = (value: number, target: number, radius: number): [number, number] => {
        // For a half circle, we only use half the circumference
        const circumference = Math.PI * radius;

        // // If value is greater than or equal to target, fill the whole half-circle
        // if (value >= target) {
        //   return [circumference, 0]; // This creates a complete half-circle with no gaps
        // }

        // Otherwise, fill based on normalized value
        const normalizedValue = normalizeValue(value);
        return [normalizedValue * circumference, circumference];
    };

    // Handle mouse events for the arc paths
    const handleMouseEnter = (metric: any) => {
        setHoveredMetric(metric);
    };

    const handleMouseLeave = () => {
        setHoveredMetric(null);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        const rect = chartContainerRef.current?.getBoundingClientRect();
        if (rect) {
            setMousePosition({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            });
        }
    };

    return (
        <div className="attainment-chart">
            <div className="attainment-header">
                <h3 className="attainment-title">Metric Attainment</h3>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
                {/* Chart */}
                <div className="chart-container" ref={chartContainerRef} style={{ position: 'relative' }}>
                    <svg width="100%" height="100%" viewBox="0 0 250 200">
                        {/* Circular tracks, progress, and target indicators */}
                        {metrics.map((metric, index) => {
                            const radius = metric.radius;

                            // Calculate the stroke-dasharray
                            const [dashLength, dashGap] = calculateStrokeDashArray(
                                metric.value,
                                metric.target,
                                radius
                            );

                            // Get normalized target position (0-1 scale)
                            const normalizedTarget = normalizeValue(metric.target);

                            // Calculate angle for target marker (π radians is left edge, 0 radians is right edge)
                            // Map normalized target value to the correct angle on semi-circle
                            const targetAngle = Math.PI * (1 - normalizedTarget);

                            return (
                                <g key={index}>
                                    {/* Background semi-circle */}
                                    <path
                                        d={`M ${125 - radius} 170 A ${radius} ${radius} 0 0 1 ${
                                            125 + radius
                                        } 170`}
                                        fill="none"
                                        stroke={metric.color}
                                        strokeWidth={metric.strokeWidth}
                                        strokeOpacity="0.2"
                                        onMouseEnter={() => handleMouseEnter(metric)}
                                        onMouseLeave={handleMouseLeave}
                                        onMouseMove={handleMouseMove}
                                        style={{ cursor: 'pointer' }}
                                    />

                                    {/* Progress arc - using stroke-dasharray */}
                                    <path
                                        d={`M ${125 - radius} 170 A ${radius} ${radius} 0 0 1 ${
                                            125 + radius
                                        } 170`}
                                        fill="none"
                                        stroke={metric.color}
                                        strokeWidth={metric.strokeWidth}
                                        strokeDasharray={`${dashLength} ${dashGap}`}
                                        strokeDashoffset="0"
                                        onMouseEnter={() => handleMouseEnter(metric)}
                                        onMouseLeave={handleMouseLeave}
                                        onMouseMove={handleMouseMove}
                                        style={{ cursor: 'pointer' }}
                                    />

                                    {/* Target marker - dot at target position */}
                                    <circle
                                        cx={125 + radius * Math.cos(targetAngle)}
                                        cy={170 - radius * Math.sin(targetAngle)}
                                        r="4"
                                        fill={metric.color}
                                        stroke="white"
                                        strokeWidth="1.5"
                                    />
                                </g>
                            );
                        })}

                        {/* Min/Max value labels */}
                        <text x="-15" y="187" className="min-max-label" textAnchor="start">
                            Min: {formatValue(minValue)}
                        </text>
                        <text x="270" y="187" className="min-max-label" textAnchor="end">
                            Max: {formatValue(maxValue)}
                        </text>
                    </svg>

                    {/* Hover tooltip - shows both current and target values */}
                    {hoveredMetric && (
                        <div
                            className="hover-tooltip"
                            style={{
                                position: 'absolute',
                                left: `${mousePosition.x}px`,
                                top: `${mousePosition.y - 80}px`,
                                backgroundColor: 'white',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                zIndex: 100,
                                pointerEvents: 'none',
                                whiteSpace: 'nowrap',
                                transform: 'translate(-50%, 0)',
                            }}
                        >
                            <div
                                className="tooltip-title"
                                style={{
                                    color: hoveredMetric.color,
                                    fontWeight: 'bold',
                                    marginBottom: '4px',
                                }}
                            >
                                {hoveredMetric.name}
                            </div>
                            <div className="tooltip-value">Value: {formatValue(hoveredMetric.value)}</div>
                            <div className="tooltip-value">Target: {formatValue(hoveredMetric.target)}</div>
                            {hoveredMetric.value >= hoveredMetric.target && (
                                <div
                                    className="tooltip-target-met"
                                    style={{ fontStyle: 'italic', color: '#4caf50' }}
                                >
                                    Target achieved!
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Legend */}
                <div className="attainment-legend">
                    {metrics.map((metric, index) => (
                        <div key={index} className="legend-item">
                            <div className="legend-color" style={{ backgroundColor: metric.color }} />
                            <div className="legend-text">
                                <div className="legend-name">{metric.name}</div>
                                <div className="legend-value">Metric Value: {formatValue(metric.value)}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AttainmentChart;
